import { useMemo, useState } from 'react'
import { MAX_PARTS, MAX_RAMP_STEPS, PRESETS, planBatches, planRamp } from '../exportBatches'
import { bytes, n } from '../format'

const CLICK_GAP_MS = 400

/**
 * Export the rows in view: one file, a batch per file for tools that cap how
 * much you can import at once, or a ramp — the first few files at hand-picked
 * sizes (100, then 2,000, then 5,000…) and every file after that at one equal
 * size, the way a warm-up schedule wants them.
 *
 * Every batch is a complete CSV — same header, same columns — holding the
 * view's rows in the order the table shows them, each row in exactly one file,
 * so the parts concatenate back into the whole export with no row lost or
 * repeated. A ramp changes only where the file boundaries fall.
 *
 * The server version handed the browser download URLs; here each file is built
 * by the engine worker and saved through the browser's own download.
 *
 * @param download (batch|null, shape?) => Promise — builds and saves one file
 * @param rows total rows in the current view, or null while that is unknown
 * @param shapes optional `[{id, label, blurb}]` — when the same rows can be
 *   written more than one way, as a comparison can (a row per record, or a row
 *   per differing value). The first is the default.
 */
export function ExportPanel({ download, rows, scanning, label, estimatedRowBytes, shapes = null }) {
  const [mode, setMode] = useState('one') // 'one' | 'equal' | 'ramp'
  const [size, setSize] = useState(10000)
  const [firsts, setFirsts] = useState(['100', '2000', '5000', ''])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [shape, setShape] = useState(shapes?.[0]?.id ?? null)

  // A shape the caller no longer offers must not linger in a request.
  const chosen = shapes?.some((s) => s.id === shape) ? shape : (shapes?.[0]?.id ?? null)

  const { perFile, ramp, parts } = useMemo(
    () => (mode === 'ramp' ? planRamp(rows, firsts, size) : { ramp: null, ...planBatches(rows, size) }),
    [mode, rows, firsts, size],
  )
  const count = parts.length
  const rampCount = ramp ? Math.min(ramp.length, count) : 0
  const tooMany = count > MAX_PARTS
  const ready = !scanning && Number.isFinite(rows)

  /**
   * The parameters for one part. Equal batches say only which part they are;
   * ramp batches also say which row they start at, since with unequal sizes
   * that cannot be worked out from the part number.
   */
  const batchParam = (p) =>
    mode === 'ramp'
      ? { size: p.rows, part: p.part, parts: count, offset: p.from - 1 }
      : { size: perFile, part: p.part, parts: count }

  const save = async (batch) => {
    setSending(true)
    setError(null)
    try {
      await download(batch, chosen)
    } catch (e) {
      setError(e.message)
    } finally {
      setSending(false)
    }
  }

  /**
   * Build and save one file per part. Chrome asks once whether this site may
   * download several files, which is the browser's call and not something the
   * page can skip; the gap keeps the prompts from racing each other.
   */
  const downloadAll = async () => {
    setSending(true)
    setError(null)
    try {
      for (const p of parts) {
        await download(batchParam(p), chosen)
        if (p.part < count) await new Promise((r) => setTimeout(r, CLICK_GAP_MS))
      }
    } catch (e) {
      setError(e.message)
    } finally {
      setSending(false)
    }
  }

  const setFirst = (i, value) => setFirsts(firsts.map((v, k) => (k === i ? value : v)))

  /** "100 + 2,000 + 5,000, then 9 × 10,000 rows" — the plan in one line. */
  const describeRamp = () => {
    const lead = parts
      .slice(0, rampCount)
      .map((p) => n(p.rows))
      .join(' + ')
    const rest = count - rampCount
    if (rest === 0) return lead
    return `${lead}, then ${n(rest)} × ${n(perFile)}`
  }

  return (
    <section className="panel">
      <header className="panel-head">
        <h2>Export</h2>
        {ready && <span className="count">{n(rows)} rows</span>}
      </header>

      {shapes && (
        <>
          <div className="mode-list">
            {shapes.map((s) => (
              <label key={s.id} className={`mode-option ${chosen === s.id ? 'on' : ''}`} title={s.blurb}>
                <input
                  type="radio"
                  name="export-rowshape"
                  checked={chosen === s.id}
                  onChange={() => setShape(s.id)}
                />
                <span className="mode-text">
                  <span className="mode-label">{s.label}</span>
                  {chosen === s.id && <span className="mode-blurb">{s.blurb}</span>}
                </span>
              </label>
            ))}
          </div>
          <hr className="panel-rule" />
        </>
      )}

      <div className="mode-list">
        <label className={`mode-option ${mode === 'one' ? 'on' : ''}`}>
          <input type="radio" name="export-shape" checked={mode === 'one'} onChange={() => setMode('one')} />
          <span className="mode-text">
            <span className="mode-label">One file</span>
            <span className="mode-blurb">Everything in view, in a single CSV.</span>
          </span>
        </label>
        <label className={`mode-option ${mode === 'equal' ? 'on' : ''}`}>
          <input
            type="radio"
            name="export-shape"
            checked={mode === 'equal'}
            onChange={() => setMode('equal')}
          />
          <span className="mode-text">
            <span className="mode-label">Split into batches</span>
            <span className="mode-blurb">One file per N rows, each with its own header — for import limits.</span>
          </span>
        </label>
        <label className={`mode-option ${mode === 'ramp' ? 'on' : ''}`}>
          <input type="radio" name="export-shape" checked={mode === 'ramp'} onChange={() => setMode('ramp')} />
          <span className="mode-text">
            <span className="mode-label">Start small, then split evenly</span>
            <span className="mode-blurb">
              The first files at sizes you pick — try 100, then 2,000, then 5,000 — and equal batches
              after that.
            </span>
          </span>
        </label>
      </div>

      {error && <div className="alert">{error}</div>}

      {mode === 'one' ? (
        <div className="panel-actions tight">
          <button className="btn primary" disabled={scanning || sending} onClick={() => save(null)}>
            {sending ? 'Building…' : label}
          </button>
        </div>
      ) : (
        <>
          {mode === 'ramp' && (
            <div className="ramp-first">
              <label className="batch-label">First files, in rows</label>
              <div className="ramp-steps">
                {Array.from({ length: MAX_RAMP_STEPS }, (_, i) => (
                  <input
                    key={i}
                    className="input"
                    type="number"
                    min={1}
                    placeholder="—"
                    title={`Rows in file ${i + 1} — leave blank to skip`}
                    value={firsts[i] ?? ''}
                    onChange={(e) => setFirst(i, e.target.value)}
                  />
                ))}
              </div>
            </div>
          )}
          <div className="batch-size">
            <label className="batch-label" htmlFor="batch-size">
              {mode === 'ramp' ? 'Then rows per file' : 'Rows per file'}
            </label>
            <input
              id="batch-size"
              className="input"
              type="number"
              min={1}
              step={1000}
              value={size}
              onChange={(e) => setSize(e.target.value)}
            />
          </div>
          <div className="panel-actions tight">
            {PRESETS.map((p) => (
              <button
                key={p}
                className={`btn tiny ${perFile === p ? 'primary' : ''}`}
                onClick={() => setSize(p)}
              >
                {n(p)}
              </button>
            ))}
          </div>

          {!ready ? (
            <p className="hint">
              {scanning ? 'Waiting for the scan to finish — the row count decides how many files.' : 'Row count not known yet.'}
            </p>
          ) : count === 0 ? (
            <p className="hint">No rows in view, so there is nothing to export.</p>
          ) : (
            <>
              <p className="hint">
                <strong>{n(count)}</strong> file{count === 1 ? '' : 's'}
                {mode === 'ramp'
                  ? ` · ${describeRamp()} rows`
                  : count > 1
                    ? ` · ${n(count - 1)} × ${n(perFile)} rows + ${n(parts[count - 1].rows)}`
                    : ` · ${n(parts[count - 1].rows)} rows`}
                {mode === 'equal' &&
                  Number.isFinite(estimatedRowBytes) &&
                  ` · ~${bytes(estimatedRowBytes * perFile)} each`}
              </p>
              {tooMany ? (
                <p className="footnote warn-text">
                  That is {n(count)} downloads. Raise the rows per file — over {n(MAX_PARTS)} files, ask for them
                  one at a time instead.
                </p>
              ) : (
                <>
                  <div className="panel-actions tight">
                    <button className="btn primary" onClick={downloadAll} disabled={sending || scanning}>
                      {sending ? 'Building downloads…' : `Download all ${n(count)}`}
                    </button>
                  </div>
                  <ol className="part-list">
                    {parts.map((p) => (
                      <li key={p.part}>
                        <button
                          className="part-link"
                          disabled={sending || scanning}
                          onClick={() => save(batchParam(p))}
                        >
                          <span className="part-name">
                            part {p.part} of {count}
                          </span>
                          <span className="part-rows">
                            rows {n(p.from)}–{n(p.to)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ol>
                  <p className="footnote">
                    Your browser may ask once whether this site may download several files. Each part is a
                    full CSV; together they are the same rows as the single file.
                  </p>
                </>
              )}
            </>
          )}
        </>
      )}
    </section>
  )
}
